/**
 * Decide whether to run the interface in its cheap mode.
 *
 * The dashboard leans on effects that are inexpensive on a modern laptop and
 * genuinely costly elsewhere. Measured across its 73 stylesheets: 257 literal
 * `backdrop-filter` declarations - each one promotes an element to its own
 * compositor layer and re-blurs the backdrop every frame - and 31 *infinite*
 * animation usages that animate non-compositor properties (15 box-shadow,
 * 13 background-position, 2 height, 1 border-color), every one of which forces
 * a repaint per frame for as long as the page is open.
 *
 * `prefers-reduced-motion` already covers people who asked the OS to calm
 * things down; src/index.css acts on it globally. This covers the ones who did
 * not, but whose hardware cannot comfortably keep up.
 *
 * Setting `perf-lite` on <html> is all this does; performance-lite.css owns
 * what that actually turns off.
 *
 * Ported from mediasfu-telephone's src/utils/performanceMode.ts - keep the two
 * in step if the heuristics change.
 */

const CLASS = 'perf-lite';
const STORAGE_KEY = 'mediasfu_perf_mode';

/** Cores at or below this suggest a low-end or heavily throttled machine. */
const LOW_CORE_COUNT = 4;
/** navigator.deviceMemory is reported in GB, capped at 8 for privacy. */
const LOW_MEMORY_GB = 4;

const readPreference = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'lite' || stored === 'full') return stored;
  } catch {
    // Private mode or blocked storage - fall through to auto.
  }
  return 'auto';
};

/** Does the device look constrained? Conservative: only obvious cases. */
export const detectConstrainedDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  const nav = navigator;

  if (
    typeof nav.hardwareConcurrency === 'number' &&
    nav.hardwareConcurrency > 0 &&
    nav.hardwareConcurrency <= LOW_CORE_COUNT
  ) {
    return true;
  }

  if (
    typeof nav.deviceMemory === 'number' &&
    nav.deviceMemory > 0 &&
    nav.deviceMemory <= LOW_MEMORY_GB
  ) {
    return true;
  }

  // A display that cannot repaint quickly will not benefit from any of this.
  if (typeof window.matchMedia === 'function' && window.matchMedia('(update: slow)').matches) {
    return true;
  }

  return false;
};

/** Apply the resolved mode to <html>. Safe to call more than once. */
export const applyPerformanceMode = () => {
  if (typeof document === 'undefined') return false;

  const preference = readPreference();
  const lite = preference === 'lite' || (preference === 'auto' && detectConstrainedDevice());

  document.documentElement.classList.toggle(CLASS, lite);
  return lite;
};

/**
 * Override the automatic decision. 'auto' clears the override.
 * Exposed so a settings screen - or support walking a customer through it -
 * can force either mode.
 */
export const setPerformanceMode = (preference) => {
  try {
    if (preference === 'auto') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Non-fatal: the class still gets applied for this session.
  }
  return applyPerformanceMode();
};

export const isPerformanceModeActive = () =>
  typeof document !== 'undefined' && document.documentElement.classList.contains(CLASS);
